import React, { useState } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import NursingHomeOrderConfirmationMobile from "./NursingHomeOrderConfirmationMobile";
import {
  Box,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import HomeIcon from "@mui/icons-material/Home";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { downloadOrderSummary } from "./orderDownloadUtils";
import logger from "../../utilitis/logger";
import TodaysOrdersDialog, { useTodaysOrdersCheck } from "./TodaysOrdersDialog";

const NursingHomeOrderConfirmation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [orderReference, setOrderReference] = useState("");
  const [orderName, setOrderName] = useState("");
  const { runCheck, checking, dialogProps: todaysOrdersDialogProps } = useTodaysOrdersCheck();

  if (isMobile) return <NursingHomeOrderConfirmationMobile />;

  // Get data passed from order form
  const orderState = location.state;

  // If no state was passed, redirect back to form
  if (!orderState || !orderState.formData) {
    return <Navigate to="/nursinghome" replace />;
  }

  const { formData, contactName, phoneNum, products, deliveryDays, sites } =
    orderState;

  // Find product details by partname
  const getProductDetails = (partname) => {
    return products?.find((p) => p.PARTNAME === partname);
  };

  // Format delivery date for display
  const getDeliveryDateLabel = () => {
    if (!formData.dueDate) return "לא נבחר";
    const dateStr = formData.dueDate.split("T")[0];
    const match = deliveryDays?.find((d) => d.date === dateStr);
    if (match) return match.formatted;
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  // Submit order to API
  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await axios.post(
        "/nursinghome/submit-order",
        {
          reference: formData.reference,
          dueDate: formData.dueDate,
          items: formData.items,
          notes: formData.notes,
          driverText: formData.driverText || "",
          files: formData.selectedFiles,
          dcode: formData.dcode || undefined,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.success) {
        setOrderReference(response.data.orderReference);
        setOrderName(response.data.orderName || "");
        setSuccessDialogOpen(true);
      }
    } catch (err) {
      logger.error("Error submitting order:", err);
      if (err.response?.status === 403) {
        setError("אין הרשאה לשלוח הזמנה. אנא התחבר מחדש.");
      } else if (err.response?.status === 400) {
        setError("הזמנה לא הצליחה להקלט למערכת, נא לפנות לשירות לקוחות");
      } else if (err.response?.status === 404) {
        setError("לא ניתן למצוא את נתוני הלקוח. אנא פנה לתמיכה.");
      } else if (err.response?.status === 409) {
        setError(
          "לא ניתן לפתוח הזמנה כיוון שהזמנה עם מספר זה כבר קיימת במערכת. אנא השתמש במספר הזמנת רכש אחר.",
        );
      } else if (err.response?.status >= 500) {
        setError("שגיאת שרת בעת שליחת ההזמנה. אנא נסה שוב מאוחר יותר.");
      } else if (
        err.code === "ECONNABORTED" ||
        err.message.includes("timeout")
      ) {
        setError("תם הזמן הקצוב לשליחת ההזמנה. נסה שוב.");
      } else if (!navigator.onLine) {
        setError("אין חיבור לאינטרנט. אנא בדוק את החיבור שלך.");
      } else if (err.response?.data?.msg) {
        setError(`שגיאה: ${err.response.data.msg}`);
      } else {
        setError(
          `שגיאה בשליחת ההזמנה: ${err.message || "נסה שוב מאוחר יותר"}`,
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Go back to form with data preserved
  const handleGoBack = () => {
    navigate("/nursinghome", {
      state: { formData },
    });
  };

  // Success dialog actions
  const handleNewOrder = () => {
    setSuccessDialogOpen(false);
    navigate("/nursinghome");
  };

  const handleBackToHome = () => {
    navigate("/portal");
  };

  return (
    <Container maxWidth="lg" sx={{ pb: 8 }}>
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#1976d2" }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  סיכום הזמנה
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  אנא בדוק/י את פרטי ההזמנה לפני השליחה
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Order Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              פרטי הזמנה
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {formData.reference && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    מס' הזמנת רכש:
                  </Typography>
                  <Typography variant="body1">{formData.reference}</Typography>
                </Box>
              )}
              {formData.dcode && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    אתר אספקה:
                  </Typography>
                  <Typography variant="body1">
                    {sites?.find((s) => s.CODE === formData.dcode)?.CODEDES ||
                      formData.dcode}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  צפי ליום הפצה:
                </Typography>
                <Typography variant="body1">
                  {getDeliveryDateLabel()}
                </Typography>
              </Box>
              {formData.notes && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    הערות:
                  </Typography>
                  <Typography variant="body1">{formData.notes}</Typography>
                </Box>
              )}
              {formData.driverText && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    הוראות לנהג:
                  </Typography>
                  <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
                    {formData.driverText}
                  </Typography>
                </Box>
              )}
              {formData.selectedFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    קבצים מצורפים:
                  </Typography>
                  {formData.selectedFiles.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <AttachFileIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Items Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              פריטים בהזמנה ({formData.items.length} פריטים)
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>מס'</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>מק"ט</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>תיאור מוצר</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>כמות</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => {
                    const product = getProductDetails(item.partname);
                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.partname}</TableCell>
                        <TableCell>{product?.PARTDES || "-"}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            disabled={submitting || checking}
            sx={{ minWidth: 160 }}
          >
            חזור למסך קודם
          </Button>
          <Button
            variant="contained"
            startIcon={submitting || checking ? null : <SendIcon />}
            onClick={() => runCheck(handleConfirm)}
            disabled={submitting || checking}
            sx={{ minWidth: 120 }}
          >
            {submitting || checking ? <CircularProgress size={24} /> : "אישור ושליחה"}
          </Button>
        </Box>

        {/* Success Dialog */}
        <Dialog
          open={successDialogOpen}
          onClose={() => setSuccessDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ direction: "ltr", pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "green" }}>
              ההזמנה נקלטה בהצלחה
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ direction: "ltr", pt: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ direction: "ltr" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  מספר הזמנת רכש:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {orderReference}
                </Typography>
              </Box>

              {orderName && (
                <Box sx={{ direction: "ltr" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    מספר הזמנה במערכת:
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#e3f2fd",
                      border: "2px solid #1976d2",
                      padding: 2,
                      borderRadius: 2,
                      textAlign: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: "primary.main",
                        fontWeight: 700,
                        letterSpacing: 1,
                      }}
                    >
                      {orderName}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Alert severity="warning" sx={{ direction: "ltr" }}>
                <Typography variant="body2">
                  לעדכונים/שינויים בהזמנה יש לפנות לצוות שירות הלקוחות במייל /
                  בטלפון.
                </Typography>
              </Alert>

              {orderName && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    downloadOrderSummary({
                      orderName,
                      orderReference,
                      formData,
                      products,
                      deliveryDays,
                      sites,
                    })
                  }
                  sx={{ py: 1.5 }}
                >
                  הורד מספר הזמנה
                </Button>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              direction: "rtl",
              justifyContent: "flex-start",
              px: 3,
              pb: 2,
            }}
          >
            <Button
              onClick={handleNewOrder}
              variant="contained"
              startIcon={<AddShoppingCartIcon sx={{ ml: 1 }} />}
              sx={{ ml: 1 }}
            >
              צור הזמנה חדשה
            </Button>
            <Button
              onClick={handleBackToHome}
              variant="outlined"
              startIcon={<HomeIcon sx={{ ml: 1 }} />}
            >
              חזור לדף הבית
            </Button>
          </DialogActions>
        </Dialog>

        {/* Today's Orders Confirmation Dialog */}
        <TodaysOrdersDialog {...todaysOrdersDialogProps} />
      </Box>
    </Container>
  );
};

export default NursingHomeOrderConfirmation;
